/**
 * Product Lightbox Modal
 *
 * Handles product detail modal with image carousel
 * @package twentytwentyfour-child
 */

import Swiper from 'swiper';
import { Navigation, Pagination, Keyboard } from 'swiper/modules';

/**
 * Initialize product lightbox functionality
 */
export function initProductLightbox() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupLightboxes);
    } else {
        setupLightboxes();
    }
}

/**
 * Set up lightbox event listeners
 */
function setupLightboxes() {
    // Find all product cards
    const productCards = document.querySelectorAll('.product-card-flat');

    productCards.forEach(card => {
        const viewDetailsBtn = card.querySelector('.product-card-btn');

        if (viewDetailsBtn) {
            viewDetailsBtn.addEventListener('click', (e) => {
                e.preventDefault();
                openLightbox(card);
            });
        }

        // Make entire card clickable
        card.style.cursor = 'pointer';
        card.addEventListener('click', (e) => {
            // Don't trigger if clicking the button directly (already handled above)
            if (!e.target.classList.contains('product-card-btn')) {
                openLightbox(card);
            }
        });
    });
}

/**
 * Open lightbox with product details
 */
function openLightbox(cardElement) {
    const productIndex = cardElement.dataset.productIndex;
    const carouselId = cardElement.dataset.carouselId;

    if (!productIndex || !carouselId) {
        console.warn('Product index or carousel ID not found');
        return;
    }

    // Get product data from JSON script tag
    const productDataElement = document.getElementById(`product-data-${carouselId}`);

    if (!productDataElement) {
        console.warn(`Product data not found for carousel: ${carouselId}`);
        return;
    }

    let products;
    try {
        products = JSON.parse(productDataElement.textContent);
    } catch (e) {
        console.error('Error parsing product data:', e);
        return;
    }

    const product = products[productIndex];

    if (!product) {
        console.warn(`Product not found at index: ${productIndex}`);
        return;
    }

    // Get modal element
    const modal = document.getElementById(`modal-${carouselId}`);

    if (!modal) {
        console.warn(`Modal not found: modal-${carouselId}`);
        return;
    }

    // Populate modal content
    populateModal(modal, product, carouselId);

    // Show modal
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden'; // Prevent background scrolling

    // Set up close handlers
    setupCloseHandlers(modal, carouselId);
}

/**
 * Decode HTML entities in a string
 */
function decodeHTMLEntities(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

/**
 * Populate modal with product data
 */
function populateModal(modal, product, carouselId) {
    // Populate text content using icr- prefixed classes
    const titleElement = modal.querySelector('.icr-product-title');
    let storeNameElement = modal.querySelector('.icr-product-store-name');
    let centerNameElement = modal.querySelector('.icr-product-center-name');
    let priceElement = modal.querySelector('.icr-product-price');
    const availabilityElement = modal.querySelector('.icr-product-availability');
    const descriptionElement = modal.querySelector('.icr-product-description');
    const skuElement = modal.querySelector('.icr-product-sku');
    const buyButton = modal.querySelector('.lightbox-button-buy');

    // Fallback: Create elements if they don't exist (template cache issue)
    if (!storeNameElement && titleElement) {
        storeNameElement = document.createElement('p');
        storeNameElement.className = 'icr-product-store-name';
        titleElement.parentNode.insertBefore(storeNameElement, titleElement.nextSibling);
    }

    if (!centerNameElement && storeNameElement) {
        centerNameElement = document.createElement('p');
        centerNameElement.className = 'icr-product-center-name';
        storeNameElement.parentNode.insertBefore(centerNameElement, storeNameElement.nextSibling);
    }

    if (!priceElement && centerNameElement) {
        priceElement = document.createElement('p');
        priceElement.className = 'icr-product-price';
        centerNameElement.parentNode.insertBefore(priceElement, centerNameElement.nextSibling);
    }

    if (titleElement) {
        const title = product.product_title || '';
        titleElement.textContent = title ? decodeHTMLEntities(title) : '';
    }

    // Store name (underneath title) - in uppercase, with optional link
    if (storeNameElement) {
        const storeName = product.product_store_name || '';
        const storeUrl = product.product_store_url || '';

        if (storeName) {
            const decodedStoreName = decodeHTMLEntities(storeName).toUpperCase();
            if (storeUrl) {
                storeNameElement.innerHTML = `<a href="${storeUrl}" target="_blank" rel="noopener">${decodedStoreName}</a>`;
            } else {
                storeNameElement.textContent = decodedStoreName;
            }
        } else {
            storeNameElement.textContent = '';
        }
    }

    // Retail center names (underneath store name) with "Available at:" prefix
    if (centerNameElement) {
        const productCenters = product.product_centers || [];
        if (productCenters.length > 0) {
            const centerLinks = productCenters.map(center => {
                const centerName = decodeHTMLEntities(center.name || '');
                const centerUrl = center.url || ''; // Use individual center URL from ACF repeater

                // Only create link if center URL exists
                if (centerUrl) {
                    return `<a href="${centerUrl}" class="icr-product-store-link" target="_blank" rel="noopener">${centerName}</a>`;
                } else {
                    return centerName; // Display text only if no URL
                }
            }).filter(link => link).join(', ');

            if (centerLinks) {
                centerNameElement.innerHTML = `<span class="icr-availability-label">Available at:</span> ${centerLinks}`;
            } else {
                centerNameElement.textContent = '';
            }
        } else {
            centerNameElement.textContent = '';
        }
    }

    // Price - format and populate
    if (priceElement) {
        const price = product.product_price || '';
        console.log('Product price data:', price, 'for product:', product.product_title);
        if (price) {
            // Add dollar sign if not already present
            const formattedPrice = price.toString().startsWith('$') ? price : `$${price}`;
            priceElement.innerHTML = `<strong>${formattedPrice}</strong>`;
            console.log('Price element updated with:', formattedPrice);
        } else {
            priceElement.textContent = '';
            console.log('No price data available');
        }
    } else {
        console.log('Price element not found in modal');
    }

    // Availability with dynamic store links (all centers)
    if (availabilityElement) {
        const productCenters = product.product_centers || [];
        if (productCenters.length > 0) {
            const centerLinks = productCenters.map(center => {
                const centerName = center.name || '';
                const centerUrl = center.url || '#';
                return `<a href="${centerUrl}" class="icr-product-store-link">${centerName}</a>`;
            }).filter(link => link).join(', ');

            if (centerLinks) {
                availabilityElement.innerHTML = `Available at: ${centerLinks}`;
            } else {
                availabilityElement.textContent = '';
            }
        } else {
            availabilityElement.textContent = '';
        }
    }

    if (descriptionElement) descriptionElement.innerHTML = product.product_description || '';
    if (skuElement) skuElement.textContent = product.product_sku ? `SKU: ${product.product_sku}` : '';

    // Check if product is in "Food & Beverage" category
    const categories = product.product_categories || [];
    const isFoodBeverage = categories.some(cat => {
        const categoryLower = (cat || '').toString().toLowerCase();
        return categoryLower.includes('food') || categoryLower.includes('beverage');
    });

    // Hide all CTA buttons for Food & Beverage products
    const buttonsContainer = modal.querySelector('.icr-product-buttons');
    if (buttonsContainer) {
        if (isFoodBeverage) {
            buttonsContainer.style.display = 'none';
        } else {
            buttonsContainer.style.display = 'flex';

            // Handle "Buy online now" button - show only if product URL exists
            if (buyButton) {
                const buyButtonWrapper = buyButton.closest('.wp-block-button');
                if (product.product_url && product.product_url.trim() !== '') {
                    buyButton.href = product.product_url;
                    if (buyButtonWrapper) {
                        buyButtonWrapper.style.display = 'block';
                    }
                } else {
                    if (buyButtonWrapper) {
                        buyButtonWrapper.style.display = 'none';
                    }
                }
            }
        }
    }

    // Initialize image carousel
    initLightboxSwiper(modal, product, carouselId);
}

/**
 * Initialize Swiper for lightbox image carousel
 */
function initLightboxSwiper(modal, product, carouselId) {
    const swiperElement = modal.querySelector(`.lightbox-swiper-${carouselId}`);
    const swiperWrapper = swiperElement?.querySelector('.swiper-wrapper');

    if (!swiperElement || !swiperWrapper) {
        console.warn('Swiper elements not found in modal');
        return;
    }

    // Clear existing slides
    swiperWrapper.innerHTML = '';

    // Get product images
    const images = product.product_images || [];

    if (images.length === 0) {
        // Add placeholder if no images
        swiperWrapper.innerHTML = `
            <div class="swiper-slide">
                <div style="background: #f0f0f0; height: 100%; display: flex; align-items: center; justify-content: center;">
                    <p style="color: #666;">No image available</p>
                </div>
            </div>
        `;
    } else {
        // Add image slides
        images.forEach(image => {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide';
            slide.innerHTML = `
                <img src="${image.url || ''}"
                     alt="${image.alt || product.product_title || ''}"
                     loading="lazy"
                     decoding="async">
            `;
            swiperWrapper.appendChild(slide);
        });
    }

    // Destroy existing swiper if present
    if (swiperElement.swiper) {
        swiperElement.swiper.destroy(true, true);
    }

    // Initialize new Swiper
    const swiper = new Swiper(swiperElement, {
        modules: [Navigation, Pagination, Keyboard],
        loop: false,
        pagination: {
            el: swiperElement.querySelector('.swiper-pagination'),
            type: 'bullets',
            clickable: true,
        },
        navigation: {
            nextEl: swiperElement.querySelector('.swiper-button-next'),
            prevEl: swiperElement.querySelector('.swiper-button-prev'),
        },
        grabCursor: true,
        keyboard: {
            enabled: true,
        },
    });

    return swiper;
}

/**
 * Set up modal close handlers
 */
function setupCloseHandlers(modal, carouselId) {
    const closeButton = modal.querySelector('.lightbox-close');
    const overlay = modal.querySelector('.lightbox-overlay');

    // Close button click
    if (closeButton) {
        closeButton.onclick = () => closeLightbox(modal);
    }

    // Overlay click
    if (overlay) {
        overlay.onclick = () => closeLightbox(modal);
    }

    // Escape key
    const escapeHandler = (e) => {
        if (e.key === 'Escape') {
            closeLightbox(modal);
            document.removeEventListener('keydown', escapeHandler);
        }
    };
    document.addEventListener('keydown', escapeHandler);
}

/**
 * Close the lightbox modal
 */
function closeLightbox(modal) {
    modal.setAttribute('hidden', 'hidden');
    document.body.style.overflow = ''; // Restore scrolling

    // Destroy swiper to prevent memory leaks
    const swiperElement = modal.querySelector('.swiper');
    if (swiperElement && swiperElement.swiper) {
        swiperElement.swiper.destroy(true, true);
    }
}

// Auto-initialize on module load
initProductLightbox();
